import { Column } from "primereact/column";
import { DataTable, DataTableSortEvent } from "primereact/datatable";
import { useEffect, useMemo, useRef, useState } from "react";
import UpdateItemStatusPopup from "src/components/UpdateItemStatus/UpdateItemStatusPopup";
import { useSelector, useDispatch } from "react-redux";
import {
  getMaintenanceRequests,
  retrieveMaintenanceRequestByTicketNumber,
  selectMaintenanceRequests,
  setIsMaintenanceRequestsDetailsPopupOpen,
  setGridCurrentPage,
  setNumberOfRowsPerPage,
  setFilterDataField,
  retrieveMaintenanceStatusForEdit,
} from "src/redux/slices/maintenanceRequestsSlice";
import MaintenanceRequestsDetailsPopup from "./MaintenanceRequestsDetailsPopup";
import { useTranslation } from "react-i18next";
import { getAssignedBuildings } from "src/redux/slices/assignBuildingSlice";
import { PopupType } from "src/resources/enum";
import { formatDateV2, getTextValueById, isArabic } from "src/utils/helpers";
import {
  setIsUpdateStatusPopupOpen,
  setSelectedId,
  setPopupType,
  setOnHoldReason,
  setPreSelectedStatus,
} from "src/redux/slices/sharedSlice";
import SLATemplate from "src/components/SLABreached/SLATemplate";
import GridPaginator from "src/components/GridPaginator/GridPaginator";
import StatusColumnTemplate from "src/components/UpdateItemStatus/StatusColumnTemplate";
import { SortOrderDefault } from "src/resources/constant";
import { retrieveAppointmentStatusForEdit } from "src/redux/slices/preventiveAppointmentSlice";

const MaintenanceRequestsGrid = () => {
  const dispatch = useDispatch();
  const [sortOrder, setSortOrder] = useState<any | null>(SortOrderDefault);
  const { isUpdateStatusPopupOpen } = useSelector((state: any) => state.shared);
  const maintenanceRequestsData = useSelector(selectMaintenanceRequests);
  const gridCurrentPage = useSelector(
    (state: any) => state.maintenanceRequests.gridCurrentPage
  );
  const { totalRowsCount, numberOfRowsPerPage } = useSelector(
    (state: any) => state.maintenanceRequests
  );
  const { statusOptions } = useSelector((state: any) => state.lookup);
  const isMaintenanceRequestsDetailsPopupOpen = useSelector(
    (state: any) =>
      state.maintenanceRequests.isMaintenanceRequestsDetailsPopupOpen
  );
  const { allServiceTerritories, systems } = useSelector(
    (state: any) => state.lookup
  );

  const { sortColumn } = useSelector(
    (state: any) => state.maintenanceRequests.filterData
  );

  const maintenanceStatusForEdit = useSelector(
    (state: any) => state.maintenanceRequests.maintenanceStatusForEdit
  );

  const { t } = useTranslation(["common"]);
  const tableRef = useRef<DataTable<(typeof maintenanceRequestsData)[0]>>(null);

  const successCallback = () => {
    dispatch(getMaintenanceRequests() as any);
  };

  useEffect(() => {
    dispatch(getAssignedBuildings() as any);
    if (!maintenanceStatusForEdit.length) {
      dispatch(retrieveMaintenanceStatusForEdit() as any);
    }
  }, [dispatch]);

  const setUpdateStatusPopupOpen = (rowData: any) => {
    dispatch(setIsUpdateStatusPopupOpen(true));
    dispatch(setPopupType(PopupType.MaintenanceRequest));
    dispatch(setSelectedId(rowData.id));
    dispatch(setPreSelectedStatus(rowData.statusCodeId));
    dispatch(setOnHoldReason(rowData.onHoldReasonId));
  };

  const caseIdTemplate = (rowData: any) => {
    return (
      <div onClick={() => handleBuildingNumberClick(rowData)}>
        {rowData.caseID}
      </div>
    );
  };

  const GridSLATemplate = (rowData: any) => {
    return <SLATemplate sLABreached={rowData.sLABreached} />;
  };

  const StatusTemplate = (rowData: any) => {
    return (
      <StatusColumnTemplate
        statuses={statusOptions}
        rowData={rowData}
        setUpdateStatusPopupOpen={setUpdateStatusPopupOpen}
      />
    );
  };

  const serviceTerritoryTemplate = (rowData: any) => {
    return (
      <span>
        {getTextValueById(
          rowData?.serviceTerritoryId,
          allServiceTerritories as unknown as [],
          "territoryId"
        )}
      </span>
    );
  };
  const systemTypeTemplate = (rowData: any) => {
    return (
      <span>
        {getTextValueById(
          rowData?.systemServiceId,
          systems as unknown as [],
          "systemServiceId"
        )}
      </span>
    );
  };

  const handleBuildingNumberClick = (rowData: any) => {
    dispatch(
      retrieveMaintenanceRequestByTicketNumber(rowData.caseID) as any
    ).then(() => {
      dispatch(setIsMaintenanceRequestsDetailsPopupOpen(true));
    });
  };

  const createdOnTemplate = (rowData: any) => {
    return (
      <span className="plain-text-date">{formatDateV2(rowData.createdOn)}</span>
    );
  };

  const onSort = (e: DataTableSortEvent) => {
    let sortFieldBEName = null;
    setSortOrder(e.sortOrder);
    if (e.sortField === "propertyNumber") sortFieldBEName = "propertyID";
    if (e.sortField === "status") sortFieldBEName = "statusId";
    else if (e.sortField === "territory")
      sortFieldBEName = "serviceTerritoryId";
    dispatch(
      setFilterDataField({
        field: "sortColumn",
        value: sortFieldBEName ?? e.sortField,
      })
    );
    dispatch(
      setFilterDataField({ field: "isAscending", value: e.sortOrder === 1 })
    );
    dispatch(setGridCurrentPage(0));
  };
  return (
    <div className="grid-outer-wrapper">
      <div className="grid-inner-wrapper">
        <DataTable
          ref={tableRef}
          lazy
          className="maintenance-requests-grid"
          value={maintenanceRequestsData}
          emptyMessage={t("emptyRecords")}
          sortField={sortColumn || undefined}
          sortOrder={sortOrder || null} // Ensure sortOrder is a number or null
          onSort={onSort}
          scrollable
        >
          <Column
            field="caseID"
            header={t("caseID")}
            body={caseIdTemplate}
            className="case-id-col lg-col fixed-col"
            frozen
            alignFrozen={isArabic() ? "right" : "left"}
          ></Column>
          <Column
            field="buildingName"
            header={t("buildingNameHeader")}
            sortable
            className="lg-col"
          ></Column>
           <Column
            field="propertyTradeLicense"
            header={t("propertyTradeLicense")}
            // sortable
            
            className="lg-col"
          ></Column>

             <Column
            field="pRPNumber"
            header={t("PRPNumber")}
            // sortable
            
            className="lg-col"
          ></Column>

          <Column
            field="statusCodeId"
            header={t("status")}
            sortable
            body={StatusTemplate}
            className="md-col"
          ></Column>
          <Column
            field="sLABreached"
            header={t("sLABreached")}
            sortable
            body={GridSLATemplate}
            className="md-col"
          ></Column>

          <Column
            field="creationDate"
            header={t("creationDate")}
            body={createdOnTemplate}
            sortable
            className="plain-text-date md-col"
          ></Column>
          <Column
            field="systemName"
            header={t("systemName")}
            body={systemTypeTemplate}
            className="md-col"
          ></Column>

          <Column
            field="systemSubtype"
            header={t("systemSubtype")}
            className="md-col"
          ></Column>
          <Column
            field="faultLocation"
            header={t("faultLocation")}
            className="md-col"
          ></Column>
          <Column
            field="faultDescription"
            header={t("faultDescription")}
            className="md-col"
          ></Column>
          <Column
            field="bldgContactPerson"
            header={t("bldgContactPerson")}
            className="lg-col"
          ></Column>
          <Column field="phone" header={t("phone")} className="sm-col"></Column>
          <Column
            field="latitude"
            header={t("latitude")}
            className="sm-col"
          ></Column>
          <Column
            field="longitude"
            header={t("longitude")}
            className="sm-col"
          ></Column>
          <Column
            field="territory"
            header={t("territory")}
            // sortable
            body={serviceTerritoryTemplate}
            className="lg-col"
          ></Column>
            

           
          
        </DataTable>
      </div>
      <GridPaginator
        totalRowsCount={totalRowsCount}
        currentPage={gridCurrentPage}
        numberOfRowsPerPage={numberOfRowsPerPage}
        setNumberOfRowsPerPage={setNumberOfRowsPerPage}
        setGridCurrentPage={setGridCurrentPage}
        tableRef={tableRef}
      />
      {isMaintenanceRequestsDetailsPopupOpen && (
        <MaintenanceRequestsDetailsPopup />
      )}
      {isUpdateStatusPopupOpen && (
        <UpdateItemStatusPopup successCallbackFn={successCallback} />
      )}
    </div>
  );
};

export default MaintenanceRequestsGrid;
